import { onCLS, onINP, onLCP, onTTFB, type Metric as WebVitalsMetric } from 'web-vitals';

type Metric = WebVitalsMetric & {
  rating: 'good' | 'needs-improvement' | 'poor';
};

/**
 * Web Vitals thresholds based on Core Web Vitals
 * https://web.dev/vitals/
 */
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint (ms)
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift (score)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
} as const;

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];
  if (!threshold) return 'needs-improvement';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

function reportMetric(metric: Metric) {
  // Log to console in development
  if (import.meta.env.DEV) {
    const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
    console.log(`${emoji} [Web Vitals] ${metric.name}: ${Math.round(metric.value)} (${metric.rating})`);
  }

  // In production, you can send to analytics service
  // Example: sendToAnalytics(metric);
  
  // For now, we'll just log to console
  // In production, integrate with your analytics service:
  // - Google Analytics
  // - Sentry
  // - Custom analytics endpoint
}

/**
 * Initialize Web Vitals tracking
 * Call this once when the app loads
 */
export function initWebVitals() {
  // Largest Contentful Paint
  onLCP((metric) => {
    reportMetric({
      ...metric,
      rating: getRating('LCP', metric.value),
    });
  });

  // Interaction to Next Paint (replaces FID)
  onINP((metric) => {
    reportMetric({
      ...metric,
      rating: getRating('INP', metric.value),
    });
  });

  // Cumulative Layout Shift
  onCLS((metric) => {
    reportMetric({
      ...metric,
      rating: getRating('CLS', metric.value),
    });
  });

  // Time to First Byte
  onTTFB((metric) => {
    reportMetric({
      ...metric,
      rating: getRating('TTFB', metric.value),
    });
  });
}

